import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type BasePageResponse } from '../models'

const _BASE_URL = import.meta.env.REACT_APP_WORKFLOWS_SERVICE_API_BASE_URL!

export type CustomWorkflowTriggerType = 'manual' | 'event'

export type CustomWorkflowInputFieldValue = string | number | boolean | null

export type CustomWorkflowJsonValue = string | number | boolean | null | CustomWorkflowJsonValue[] | { [key: string]: CustomWorkflowJsonValue }

export type CustomWorkflowContext = Record<string, CustomWorkflowJsonValue>

export interface CustomWorkflowInputField {
  variable: string
  label: string | null
  type: string
  required: boolean
  default: CustomWorkflowInputFieldValue
  options: string[]
  hidden?: boolean
}

export interface CustomWorkflowOutputField {
  variable: string
  valueType: string
}

export interface CustomWorkflowFileReference {
  transfer_method: string
  upload_file_id: string
  type?: string
}

export interface CustomWorkflowRunFile {
  uploadFileId: string
  name: string
  size: number
  extension: string
  mimeType: string
  fileReference: CustomWorkflowFileReference
}

export type CustomWorkflowRunInputValue = CustomWorkflowJsonValue | CustomWorkflowFileReference | CustomWorkflowFileReference[]

export interface CustomWorkflowSummary {
  id: string
  name: string
  description: string
  tags: string[]
  triggerType: CustomWorkflowTriggerType
}

export interface CustomWorkflowDetail extends CustomWorkflowSummary {
  inputSchema: CustomWorkflowInputField[]
  outputSchema: CustomWorkflowOutputField[]
}

export interface CustomWorkflowRunFormField {
  variable: string
  type: string
  options: string[]
  default: string
}

export interface CustomWorkflowRunFormAction {
  id: string
  title: string
  buttonStyle: string
}

export interface CustomWorkflowRunForm {
  formContent: string
  fields: CustomWorkflowRunFormField[]
  actions: CustomWorkflowRunFormAction[]
  expirationTime: number | null
}

export interface RunContextItem {
  kind: 'contact' | 'ccqExecution' | 'aiAgentResult' | 'event' | 'meeting'
  contactId?: string
  executionId?: string
  clientIds?: string[]
  aiAgentResultId?: string
  aiAgentResultKey?: string
  agentType?: string
  eventType?: string
  roomId?: string
  sessionId?: string
}

export interface RunInputValue {
  variable: string
  label: string
  type: string
  value: string | number | boolean | unknown[] | Record<string, unknown> | null
}

export interface RunInputFile {
  variable: string
  label: string
  fileName?: string | null
  relatedId?: string | null
}

export interface CurrentStep {
  nodeId: string
  nodeType: string
  title: string
  desc: string | null
  status: string
  index: number
}

export interface CustomWorkflowRun {
  id: string
  status: string
  outputs: Record<string, unknown>
  error: string | null
  pendingForm?: CustomWorkflowRunForm | null
  context?: RunContextItem[]
  inputValues?: RunInputValue[]
  files?: RunInputFile[]
  currentSteps?: CurrentStep[]
}

export interface CustomWorkflowRunSummary {
  id: string
  status: string
  createdAt: number | null
  finishedAt: number | null
  elapsedTime: number | null
  error: string | null
}

export interface AccountCustomWorkflowRunSummary {
  id: string
  workflowId: string
  workflowName: string
  status: string
  isSubRun?: boolean
  childCount?: number
  createdAt: number | null
  finishedAt: number | null
  elapsedTime: number | null
  error: string | null
  context?: RunContextItem[]
}

export type RunRelation = { type: 'primary' } | { type: 'viaCcq'; executionId: string } | { type: 'viaMeeting'; roomId: string; sessionId: string }

export interface EntityCustomWorkflowRunSummary extends AccountCustomWorkflowRunSummary {
  relation: RunRelation
}

export interface AccountEnabledCustomWorkflow {
  id: string
  customWorkflowId: string
  createdAt: string
  updatedAt: string
}

const DEFAULT_PAGE_SIZE = 100

export const customWorkflowsApi = createApi({
  reducerPath: 'customWorkflowsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['EnabledCustomWorkflows', 'CustomWorkflowRuns', 'CustomWorkflowRun'],
  endpoints: (builder) => ({
    listCustomWorkflows: builder.query<
      CustomWorkflowSummary[],
      { accountId: string; scope?: string; triggerType?: CustomWorkflowTriggerType; page?: number; size?: number }
    >({
      query: ({ accountId, scope, triggerType, page = 0, size = DEFAULT_PAGE_SIZE }) => ({
        url: `/accounts/${accountId}/custom-workflows`,
        params: { ...(scope ? { scope } : {}), ...(triggerType ? { triggerType } : {}), page, size },
      }),
      transformResponse: (response: BasePageResponse<CustomWorkflowSummary[]>) => response.data,
    }),
    getCustomWorkflow: builder.query<CustomWorkflowDetail, { accountId: string; workflowId: string }>({
      query: ({ accountId, workflowId }) => ({
        url: `/accounts/${accountId}/custom-workflows/${workflowId}`,
      }),
      transformResponse: (response: { data: CustomWorkflowDetail }) => response.data,
    }),
    startCustomWorkflowRun: builder.mutation<CustomWorkflowRun, { accountId: string; workflowId: string; inputs: Record<string, CustomWorkflowRunInputValue> }>(
      {
        query: ({ accountId, workflowId, inputs }) => ({
          url: `/accounts/${accountId}/custom-workflows/${workflowId}/runs`,
          method: 'POST',
          body: { inputs },
        }),
        transformResponse: (response: { data: CustomWorkflowRun }) => response.data,
        invalidatesTags: ['CustomWorkflowRuns'],
      },
    ),
    uploadCustomWorkflowFile: builder.mutation<CustomWorkflowRunFile, { accountId: string; workflowId: string; file: File }>({
      query: ({ accountId, workflowId, file }) => {
        const body = new FormData()
        body.append('file', file)
        return { url: `/accounts/${accountId}/custom-workflows/${workflowId}/files`, method: 'POST', body }
      },
      transformResponse: (response: { data: CustomWorkflowRunFile }) => response.data,
    }),
    listCustomWorkflowRuns: builder.query<CustomWorkflowRunSummary[], { accountId: string; workflowId: string; status?: string }>({
      query: ({ accountId, workflowId, status }) => ({
        url: `/accounts/${accountId}/custom-workflows/${workflowId}/runs`,
        params: status ? { status } : undefined,
      }),
      transformResponse: (response: { data: CustomWorkflowRunSummary[] }) => response.data,
      providesTags: ['CustomWorkflowRuns'],
    }),
    getCustomWorkflowRun: builder.query<CustomWorkflowRun, { accountId: string; workflowId: string; runId: string }>({
      query: ({ accountId, workflowId, runId }) => ({
        url: `/accounts/${accountId}/custom-workflows/${workflowId}/runs/${runId}`,
      }),
      transformResponse: (response: { data: CustomWorkflowRun }) => response.data,
      providesTags: (_result, _error, arg) => [{ type: 'CustomWorkflowRun', id: arg.runId }],
    }),
    getRunFileDownloadUrl: builder.query<{ url: string }, { accountId: string; workflowId: string; runId: string; fileId: string; asAttachment?: boolean }>({
      query: ({ accountId, workflowId, runId, fileId, asAttachment }) => ({
        url: `/accounts/${accountId}/custom-workflows/${workflowId}/runs/${runId}/files/${fileId}/download-url`,
        params: asAttachment ? { asAttachment: true } : undefined,
      }),
      transformResponse: (response: { data: { url: string } }) => response.data,
    }),
    listAccountCustomWorkflowRuns: builder.query<
      BasePageResponse<AccountCustomWorkflowRunSummary[]>,
      { accountId: string; status?: string; excludeSubRuns?: boolean; page?: number; size?: number }
    >({
      query: ({ accountId, status, excludeSubRuns, page = 0, size = DEFAULT_PAGE_SIZE }) => ({
        url: `/accounts/${accountId}/custom-workflow-runs`,
        params: { ...(status ? { status } : {}), ...(excludeSubRuns ? { excludeSubRuns: true } : {}), page, size },
      }),
      providesTags: ['CustomWorkflowRuns'],
    }),
    listChildCustomWorkflowRuns: builder.query<
      BasePageResponse<AccountCustomWorkflowRunSummary[]>,
      { accountId: string; parentRunId: string; page?: number; size?: number }
    >({
      query: ({ accountId, parentRunId, page = 0, size = DEFAULT_PAGE_SIZE }) => ({
        url: `/accounts/${accountId}/custom-workflow-runs/${parentRunId}/children`,
        params: { page, size },
      }),
      providesTags: ['CustomWorkflowRuns'],
    }),
    listCustomWorkflowRunsByEntity: builder.query<
      BasePageResponse<EntityCustomWorkflowRunSummary[]>,
      { accountId: string; entityType: string; entityId: string; status?: string; page?: number; size?: number }
    >({
      query: ({ accountId, entityType, entityId, status, page = 0, size = DEFAULT_PAGE_SIZE }) => ({
        url: `/accounts/${accountId}/custom-workflow-runs/by-entity`,
        params: { entityType, entityId, ...(status ? { status } : {}), page, size },
      }),
      providesTags: ['CustomWorkflowRuns'],
    }),
    getCustomWorkflowRunForm: builder.query<CustomWorkflowRunForm, { accountId: string; workflowId: string; runId: string }>({
      query: ({ accountId, workflowId, runId }) => ({
        url: `/accounts/${accountId}/custom-workflows/${workflowId}/runs/${runId}/form`,
      }),
      transformResponse: (response: { data: CustomWorkflowRunForm }) => response.data,
    }),
    submitCustomWorkflowRunForm: builder.mutation<
      CustomWorkflowRun,
      { accountId: string; workflowId: string; runId: string; inputs: Record<string, CustomWorkflowRunInputValue>; action: string }
    >({
      query: ({ accountId, workflowId, runId, inputs, action }) => ({
        url: `/accounts/${accountId}/custom-workflows/${workflowId}/runs/${runId}/form`,
        method: 'POST',
        body: { inputs, action },
      }),
      transformResponse: (response: { data: CustomWorkflowRun }) => response.data,
      invalidatesTags: (_result, _error, arg) => ['CustomWorkflowRuns', { type: 'CustomWorkflowRun', id: arg.runId }],
    }),
    listEnabledCustomWorkflows: builder.query<AccountEnabledCustomWorkflow[], { accountId: string; page?: number; size?: number }>({
      query: ({ accountId, page = 0, size = DEFAULT_PAGE_SIZE }) => ({
        url: `/accounts/${accountId}/enabled-custom-workflows`,
        params: { page, size },
      }),
      transformResponse: (response: BasePageResponse<AccountEnabledCustomWorkflow[]>) => response.data,
      providesTags: ['EnabledCustomWorkflows'],
    }),
    enableCustomWorkflow: builder.mutation<AccountEnabledCustomWorkflow, { accountId: string; customWorkflowId: string }>({
      query: ({ accountId, customWorkflowId }) => ({
        url: `/accounts/${accountId}/enabled-custom-workflows`,
        method: 'POST',
        body: { customWorkflowId },
      }),
      transformResponse: (response: { data: AccountEnabledCustomWorkflow }) => response.data,
      invalidatesTags: ['EnabledCustomWorkflows'],
    }),
    disableCustomWorkflow: builder.mutation<void, { accountId: string; enabledCustomWorkflowId: string }>({
      query: ({ accountId, enabledCustomWorkflowId }) => ({
        url: `/accounts/${accountId}/enabled-custom-workflows/${enabledCustomWorkflowId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EnabledCustomWorkflows'],
    }),
  }),
})

export const {
  useListCustomWorkflowsQuery,
  useGetCustomWorkflowQuery,
  useLazyGetCustomWorkflowQuery,
  useStartCustomWorkflowRunMutation,
  useUploadCustomWorkflowFileMutation,
  useListCustomWorkflowRunsQuery,
  useGetCustomWorkflowRunQuery,
  useLazyGetCustomWorkflowRunQuery,
  useGetRunFileDownloadUrlQuery,
  useLazyGetRunFileDownloadUrlQuery,
  useListAccountCustomWorkflowRunsQuery,
  useListChildCustomWorkflowRunsQuery,
  useListCustomWorkflowRunsByEntityQuery,
  useGetCustomWorkflowRunFormQuery,
  useSubmitCustomWorkflowRunFormMutation,
  useListEnabledCustomWorkflowsQuery,
  useEnableCustomWorkflowMutation,
  useDisableCustomWorkflowMutation,
} = customWorkflowsApi
